import { createContext, useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { TUserSchema } from '@/schemas/user/user.schema';
import { jwtDecode } from 'jwt-decode';
import { useGetUserById } from '@/hooks/user.hooks';
import { useGetUserRolesData } from '@/hooks/user-entity-role.hooks';
import { RoleName } from '@/api/entities/user-role-type';

type AuthContextType = {
  loggedInUser?: TUserSchema;
  setLoggedInUser: Dispatch<SetStateAction<TUserSchema | undefined>>;
  primaryRole?: RoleName;
  isLimitedRole?: boolean;
  limitedRoleLoggedInUserId?: string;
};

export const AuthContext = createContext<AuthContextType | null>(null);

type AuthProviderType = {
  children: React.ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderType) => {
  const [loggedInUser, setLoggedInUser] = useState<TUserSchema>();

  const token = localStorage.getItem('token');
  let userId: string | undefined;

  if (token) {
    try {
      const decodedToken = jwtDecode<{ sub: string }>(token);
      userId = decodedToken?.sub;
    } catch {
      /* empty */
    }
  }

  const user = useGetUserById(userId);
  const { roles } = useGetUserRolesData(userId);

  // Get primary role
  const primaryRole = roles?.includes(RoleName.ADMIN)
    ? RoleName.ADMIN
    : (roles[0] as RoleName) || (sessionStorage.getItem('role') as RoleName);

  /**
   * Prepare limited rights logged in user id
   * Use userId from token directly instead of waiting for loggedInUser to be populated
   */
  const isAdmin = primaryRole === RoleName.ADMIN;
  const isModerator = primaryRole === RoleName.MODERATOR;
  const limitedRoleLoggedInUserId = isAdmin || isModerator ? undefined : userId;
  /**
   * END OF Prepare limited rights logged in user id
   */

  // Set loggedInUser when fetched user is available
  useEffect(() => {
    if (user?.data?.data) {
      setLoggedInUser(user.data.data);
    }
  }, [user?.data?.data]);

  // Keep user role in session storage for persistence for fallback
  useEffect(() => {
    if (roles?.length > 0) {
      sessionStorage.setItem('role', roles[0] as RoleName);
    }
  }, [roles]);

  return (
    <AuthContext.Provider
      value={{
        loggedInUser,
        setLoggedInUser,
        primaryRole,
        isLimitedRole: !isAdmin && !isModerator,
        limitedRoleLoggedInUserId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
